import React, { useContext, useEffect, useState } from 'react';
import { FaEye, FaEyeSlash, FaSignInAlt } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../../images/logo.png';
import { AuthContext } from '../AuthContext';
import { showSwalNotification } from '../../utils/SwalResponse';
import LoadingComponent from '../../utils/LoadingComponent';
import GoogleLoginButton from './GoogleLoginButton';
import MyWebsite from '../../utils/MyWebsite';

const forestGreen = '#228B22';

const Login = () => {
  const { userLogin, loading, currentUser, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const report = queryParams.get('rep');
  const eventId = queryParams.get('event_id');
  const issueId = queryParams.get('issue_id');
  const defaultEmail = queryParams.get('em');

  const [formData, setFormData] = useState({
    email: defaultEmail || '',
    password: '',
    website: '' 
  });

  useEffect(() => {
    if (!!currentUser && !!authToken) {
      return navigate('/');
    }
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) {
      setMessage('Bot-like behavior detected. Please try again.');
      return;
    }

    const response = await userLogin(formData);
    showSwalNotification(response);

    if (response?.status === 'success') {
      if (response?.data?.role === 'user') {
        if (report) {
          if (report === 'submit') {
            navigate('/report');
            return;
          } else if (report === 'reg_event' && eventId) {
            await navigate(`/events/${eventId}`);
            return;
          } else if (report === 'attend' && eventId) {
            await navigate(`/events/${eventId}/attendance`);
            return;
          } else if (report === 'review' && issueId) {
            await navigate(`/reviews/add/${issueId}`);
            return;
          }
        }
        navigate('/dashboard');
      } else {
        const query = report
          ? `?rep=${report}${eventId ? `&event_id=${eventId}` : issueId ? `&issue_id=${issueId}` : ''}`
          : '';
        navigate(`/${response?.data?.user_id}/verification${query}`);
      }
    } else {
      setMessage(response?.message);
    }
  };

  if (!!loading) {
    return <LoadingComponent loadingText={'Authenticating ....'} />;
  }

  return (
    <motion.div
      className="d-flex bg-none justify-content-center align-items-center vh-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="card p-4 m-2 shadow-lg"
        style={{
          width: '100%',
          maxWidth: '480px',
          borderRadius: '16px',
          border: `1px solid ${forestGreen}`,
          backgroundColor: 'rgb(225,225,225,0.5)',
        }}
        initial={{ y: 30, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Logo */}
        <div className="text-center mb-3">
          <img src={logo} alt="App Logo" style={{ width: '100px', height: 'auto' }} />
        </div>

        <h3 className="text-center mb-4" style={{ color: 'darkGreen', fontWeight: '600' }}>
          <FaSignInAlt className="mb-1" /> Login
        </h3>

        {message && <div className="alert alert-info text-center">{message}</div>}

        {/* --- GOOGLE BUTTON: PRIMARY OPTION --- */}
        <div className="mb-4">
          <GoogleLoginButton />
        </div>

        <hr className="my-3" />

        {/* --- FORM: SECONDARY OPTION --- */}
        <div className="mt-2">
          <p className="text-center text-muted small mb-3">or sign in with email</p>

          <form onSubmit={handleSubmit}>
            <MyWebsite onChange={handleChange} />

            {/* Email */}
            <motion.div className="form-group mb-3" whileFocus={{ scale: 1.02 }}>
              <label style={{ fontWeight: 500, color: '#333' }}>✉️ Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ borderColor: forestGreen }}
              />
            </motion.div>

            {/* Password */}
            <motion.div className="form-group mb-3" whileFocus={{ scale: 1.02 }}>
              <label style={{ fontWeight: 500, color: '#333' }}>🔒 Password</label>
              <div className="input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{ borderColor: forestGreen }}
                />
                <span
                  className="input-group-text"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              {/* Forgot Password Link */}
              <div className="text-center text-success fw-semibold mt-2">
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    color: 'darkGreen',
                    fontWeight: 500,
                  }}
                >
                  Forgot Password?
                </Link>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              className="btn w-100 mt-2"
              style={{
                backgroundColor: forestGreen,
                color: '#fff',
                fontWeight: 600,
                letterSpacing: 0.5,
                borderRadius: '8px',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaSignInAlt className="me-2" />
              Login
            </motion.button>
          </form>

          <div className="text-center mt-3">
            <small>
              Don't have an account?{' '}
              <Link
                to={`/register${
                  report
                    ? `?${[
                        report ? `rep=${encodeURIComponent(report)}` : '',
                        eventId ? `event_id=${encodeURIComponent(eventId)}` : '',
                        issueId ? `issue_id=${encodeURIComponent(issueId)}` : '',
                      ]
                        .filter(Boolean)
                        .join('&')}`
                    : ''
                }`}
                style={{ color: 'darkGreen', fontWeight: 500 }}
              >
                Register
              </Link>
            </small>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Login;